import { Box, Popper, alpha, ClickAwayListener, Stack } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import IconMi from 'components/common/icon/IconMi';

interface RichTextCommandMenuProps {
    quill: any;
    richTextContainerRef: any;
    onChangeStateCommandMenu: (state: boolean) => void;
}
export default function RichTextCommandMenu(props: RichTextCommandMenuProps) {
    const { t } = useBundleTranslation(['components/common/rich-text']);
    const { quill, richTextContainerRef, onChangeStateCommandMenu } = props;

    const [searchString, setSearchString] = useState<string>('');

    //is show command menu
    const [isActiveCommandMenu, setIsActiveCommandMenu] = useState<boolean>(false);
    const isActiveCommandMenuRef = useRef<boolean>(false);
    const updateIsActiveCommandMenu = useCallback((isShow: boolean) => {
        isActiveCommandMenuRef.current = isShow;
        setIsActiveCommandMenu(isShow);
    }, []);

    //selected command item index
    const [activeCommandMenuItemIndex, setActiveCommandMenuItemIndex] = useState<number>(0);
    const activeCommandMenuItemIndexRef = useRef<number>(0);
    const updateCommandMenuItemIndex = useCallback((index: number) => {
        activeCommandMenuItemIndexRef.current = index;
        setActiveCommandMenuItemIndex(index);
    }, []);

    const lastShowItemIndexRef = useRef<number>(0);

    const commandSearchStart = useRef<number>(0);
    const filteredMenuItems = useRef<any[]>([]);

    const closestScrollContainer = useRef<any>(null);

    const positionCommandMenuRef = useRef<{ x: number; y: number }>({
        x: 0,
        y: 0,
    });

    const imageFieldRef = useRef<any>(null);

    const commandList = useMemo(() => {
        const iconsAlias: any = {
            text: 'text',
            h1: 'h1',
            h2: 'h2',
            h3: 'h3',
            bullet: 'sort-list',
            ordered: 'sort-numbers',
            image: 'image',
        };
        const result: any = {};
        Object.keys(iconsAlias).forEach((key) => {
            result[key] = {
                type: key,
                label: t(`command_menu.${key}.label`),
                desc: t(`command_menu.${key}.desc`),
                icon: iconsAlias[key],
            };
        });
        return result;
    }, []);

    const defaultMenuItems = useMemo(() => {
        return [['text', /*'h1',*/ 'h2', 'h3'], ['bullet', 'ordered'], ['image']];
    }, []);

    filteredMenuItems.current = useMemo(() => {
        //assign elements data
        let menuItems = defaultMenuItems.map((group) => {
            return group.map((item) => {
                return commandList[item];
            });
        });

        //filter by search string
        menuItems = menuItems.map((group) => {
            return group.filter((item: any) => {
                return (
                    searchString == '' ||
                    item.label.toLowerCase().includes(searchString.toLowerCase()) ||
                    item.desc.toLowerCase().includes(searchString.toLowerCase())
                );
            });
        });

        let indexItem = -1;
        //set index for elements and remove empty groups
        menuItems = menuItems
            .filter((group: any) => group.length > 0)
            .map((group) => {
                return group.map((item) => {
                    indexItem++;
                    return { ...item, index: indexItem };
                });
            });

        if (menuItems.length > 0) {
            lastShowItemIndexRef.current = indexItem;
        } else {
            lastShowItemIndexRef.current = 0;
        }

        return menuItems;
    }, [searchString]);

    const showCommandMenu = (index: number) => {
        const containerBounds = quill.container.getBoundingClientRect();
        const commandLineBounds = quill.getBounds(index);
        positionCommandMenuRef.current = {
            x: containerBounds.left + commandLineBounds.left,
            y: containerBounds.top + commandLineBounds.top,
        };
        quill.formatText(index, 1, 'command', true, 'silent');
        commandSearchStart.current = index;
        updateIsActiveCommandMenu(true);
    };

    const hideCommandMenu = useCallback(() => {
        updateIsActiveCommandMenu(false);
        let [leaf, offset] = quill.getLeaf(commandSearchStart.current + 1);
        if (leaf) {
            quill.formatText(commandSearchStart.current, leaf.domNode.length, 'command', false, 'silent');
        }
    }, []);

    const addImageAction = () => {
        if (imageFieldRef.current) {
            imageFieldRef.current.value = ''; //reset value to achieve reselection same file
            imageFieldRef.current.click();
        }
    };

    const handleChangeImage = () => {
        const file = imageFieldRef.current.files[0];
        if (file) {
            const isValidImageType = /^(image\/(jpeg|png))$/.test(file.type);
            if (isValidImageType) {
                const reader = new FileReader();
                reader.onload = () => {
                    const imageUrl = reader.result;
                    const range = quill.getSelection();
                    if (range) {
                        quill.insertEmbed(range.index, 'image', imageUrl);
                        quill.setSelection(range.index + 1);
                    }
                };
                reader.readAsDataURL(file);
            }
        }
    };

    const applyCommand = (command: string) => {
        updateIsActiveCommandMenu(false);
        let [leaf, offset] = quill.getLeaf(commandSearchStart.current + 1);
        quill.deleteText(commandSearchStart.current, leaf.domNode.length, 'command', false, 'silent');

        switch (command) {
            case 'text':
                quill.format('header', null);
                quill.format('list', null);
                break;
            case 'h2':
                quill.format('header', 2);
                break;
            case 'h3':
                quill.format('header', 3);
                break;
            case 'bullet':
                quill.format('list', 'bullet');
                break;
            case 'ordered':
                quill.format('list', 'ordered');
                break;
            case 'image':
                addImageAction();
                break;
        }
    };

    const textChangeHandler = (delta: any, oldDelta: any, source: string) => {
        const retain = delta.ops[0]?.retain ?? 0;
        const insertIndex = retain ? 1 : 0;
        const typeSymbol =
            ((!retain && delta.ops.length == 1) || (retain && delta.ops.length == 2)) && delta.ops[insertIndex]?.insert;
        if (!isActiveCommandMenuRef.current) {
            //clear command styles if exist on change (like undo / redo)
            const isCommandExist = Object.values(delta.ops).some((item: any) => {
                return item?.attributes?.command;
            });
            if (isCommandExist) {
                quill.formatText(0, quill.getLength(), 'command', false, 'silent');
            }

            if (typeSymbol && delta.ops[insertIndex]?.insert == '/') {
                showCommandMenu(retain);
            }
        } else {
            //hidden By type Space in command line
            if (typeSymbol && delta.ops[insertIndex]?.insert == ' ') {
                hideCommandMenu();
            } else {
                const start = commandSearchStart.current + 1;
                let [leaf, offset] = quill.getLeaf(start);
                if (quill.getFormat().command) {
                    setSearchString(leaf.domNode.length > 1 ? leaf.text.substring(1) : '');
                } else {
                    hideCommandMenu();
                }
            }
        }
    };

    const selectionChangeHandler = (
        range: null | { index: number; length: number },
        oldRange: null | { index: number; length: number },
        source: string,
    ) => {
        if (isActiveCommandMenuRef.current && range) {
            if (range.length == 0) {
                //if cursor caret move out of command line then hide menu
                if (!quill.getFormat().command) {
                    hideCommandMenu();
                }
            } else {
                //if selection few symbols then hide menu
                hideCommandMenu();
            }
        }
    };

    useEffect(() => {
        quill.on('text-change', textChangeHandler);
        quill.on('selection-change', selectionChangeHandler);

        return () => {
            quill.off('text-change', textChangeHandler);
            quill.off('selection-change', selectionChangeHandler);
        };
    }, []);

    const keyPressHandler = useCallback((event: KeyboardEvent) => {
        switch (event.key) {
            case 'Enter':
                if (filteredMenuItems.current.length > 0) {
                    const flatArray = filteredMenuItems.current.flat();
                    const resultItem = flatArray.find((item) => item.index === activeCommandMenuItemIndexRef.current);

                    if (resultItem) {
                        applyCommand(resultItem.type);
                    } else {
                        hideCommandMenu();
                    }
                } else {
                    hideCommandMenu();
                }
                event.preventDefault();
                break;
            case 'ArrowUp':
                if (activeCommandMenuItemIndexRef.current !== 0) {
                    updateCommandMenuItemIndex(activeCommandMenuItemIndexRef.current - 1);
                } else if (lastShowItemIndexRef.current > 0) {
                    updateCommandMenuItemIndex(lastShowItemIndexRef.current);
                }
                event.preventDefault();
                break;
            case 'ArrowDown':
                if (activeCommandMenuItemIndexRef.current !== lastShowItemIndexRef.current) {
                    updateCommandMenuItemIndex(activeCommandMenuItemIndexRef.current + 1);
                } else if (lastShowItemIndexRef.current > 0) {
                    updateCommandMenuItemIndex(0);
                }
                event.preventDefault();
                break;
        }
    }, []);

    useEffect(() => {
        onChangeStateCommandMenu(isActiveCommandMenuRef.current);
        if (isActiveCommandMenuRef.current) {
            quill.root.addEventListener('keydown', keyPressHandler);
            window.addEventListener('scroll', hideCommandMenu);
            window.addEventListener('resize', hideCommandMenu);
            if (richTextContainerRef.current) {
                closestScrollContainer.current = richTextContainerRef.current.closest('.scroll-content-container');
                if (closestScrollContainer.current) {
                    closestScrollContainer.current.addEventListener('scroll', hideCommandMenu);
                }
            }
        } else {
            quill.root.removeEventListener('keydown', keyPressHandler);
            window.removeEventListener('scroll', hideCommandMenu);
            window.removeEventListener('resize', hideCommandMenu);
            if (closestScrollContainer.current) {
                closestScrollContainer.current.removeEventListener('scroll', hideCommandMenu);
            }
            updateCommandMenuItemIndex(0);
            setSearchString('');
        }
    }, [isActiveCommandMenu]);

    return (
        <>
            <Box
                component="input"
                type="file"
                accept=".jpg,.jpeg,.png"
                ref={imageFieldRef}
                sx={{
                    pointerEvents: 'none',
                    opacity: 0,
                    position: 'absolute',
                    display: 'none',
                }}
                onChange={handleChangeImage}
            />
            <Popper
                sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}
                placement={'top-start'}
                open={isActiveCommandMenu}
                anchorEl={{
                    getBoundingClientRect: () => {
                        return new DOMRect(positionCommandMenuRef.current.x, positionCommandMenuRef.current.y, 0, 0);
                    },
                }}
            >
                <ClickAwayListener
                    onClickAway={(event: Event | React.SyntheticEvent) => {
                        hideCommandMenu();
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: 'background.default',
                            borderRadius: 1,
                            boxShadow: (theme) => `0px 1px 4px 0px ${alpha(theme.palette.text.primary, 0.32)}`,
                            position: 'relative',
                        }}
                    >
                        <Stack
                            sx={{
                                width: '240px',
                                '.active': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.04) },
                            }}
                        >
                            {filteredMenuItems.current.length > 0 ? (
                                <>
                                    {filteredMenuItems.current.map((group: any[], groupIndex: number) => {
                                        return group.map((item: any, itemIndex: number) => {
                                            return (
                                                <Fragment key={item.label}>
                                                    {groupIndex > 0 && itemIndex == 0 && (
                                                        <Box
                                                            sx={{
                                                                borderTop: (theme) =>
                                                                    `1px solid ${alpha(theme.palette.text.primary, 0.16)}`,
                                                            }}
                                                        />
                                                    )}
                                                    <Stack
                                                        direction={'row'}
                                                        sx={{ py: 1, px: 1.5, cursor: 'default', userSelect: 'none' }}
                                                        className={
                                                            item.index == activeCommandMenuItemIndex
                                                                ? 'active'
                                                                : 'default'
                                                        }
                                                        onMouseEnter={() => {
                                                            updateCommandMenuItemIndex(item.index);
                                                        }}
                                                        onClick={() => {
                                                            applyCommand(item.type);
                                                        }}
                                                    >
                                                        <Stack
                                                            direction={'row'}
                                                            sx={{
                                                                width: '16px',
                                                                height: '16px',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                mt: 0.5,
                                                                mr: 1,
                                                                flexShrink: 0,
                                                                fontSize: '16px',
                                                            }}
                                                        >
                                                            <IconMi icon={item.icon} />
                                                        </Stack>
                                                        <Box>
                                                            <Box sx={{ fontSize: '13px', lineHeight: 1 }}>
                                                                {item.label}
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    color: (theme) =>
                                                                        alpha(theme.palette.text.primary, 0.64),
                                                                }}
                                                            >
                                                                {item.desc}
                                                            </Box>
                                                        </Box>
                                                    </Stack>
                                                </Fragment>
                                            );
                                        });
                                    })}
                                </>
                            ) : (
                                <Box
                                    className={'active'}
                                    sx={{ py: 1, px: 1.5, cursor: 'default', userSelect: 'none' }}
                                    onClick={hideCommandMenu}
                                >
                                    {t('command_menu_empty_results')}{' '}
                                    <Box component={'span'} sx={{ opacity: 0.64 }}>
                                        ({t('command_menu_empty_results_dismiss')})
                                    </Box>
                                </Box>
                            )}
                        </Stack>
                    </Box>
                </ClickAwayListener>
            </Popper>
        </>
    );
}
